import dayjs from "dayjs";
import da from "dayjs/locale/da";
import { ReactNode, useState } from "react";
import { FinanceTransactionPayload } from "../../../api/finance-api";
import {
  BOOKABLE_CATEGORIES,
  CATEGORY_DEFAULT_DIRECTION,
  CATEGORY_LABELS,
  oreToInput,
  parseKrToOre,
} from "../../../finance/ledger";
import { FinanceCategory, FinanceTransaction, LogeEvent, LogeMember } from "../../../types/entities";
import { Icon } from "../Icon";
import { SegmentedTabs } from "../SegmentedTabs";
import { CATEGORY_ICONS } from "./categoryMeta";

dayjs.locale(da);

/** A labelled field in the vault, mirroring Primitives' `Field` on paper. */
function VaultField({
  label,
  hint,
  children,
  htmlFor,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  htmlFor?: string;
}) {
  const caption = `[ ${label} ]`;
  const captionClass =
    "block font-mono text-[10.5px] tracking-[1.6px] uppercase text-vault-ink3 mb-2";
  return (
    <div>
      {htmlFor ? (
        <label htmlFor={htmlFor} className={captionClass}>
          {caption}
        </label>
      ) : (
        <div className={captionClass}>{caption}</div>
      )}
      {children}
      {hint && (
        <div className="font-mono text-[9.5px] tracking-[1.2px] text-vault-ink3 mt-1.5 uppercase">
          {hint}
        </div>
      )}
    </div>
  );
}

export interface TransactionFormValues {
  direction: 1 | -1;
  amount: string;
  category: FinanceCategory;
  occurredOn: string;
  note: string;
  logeEventId: number | null;
  logeMemberId: number | null;
}

export const emptyTransactionForm = (): TransactionFormValues => ({
  direction: 1,
  amount: "",
  category: "kontingent",
  occurredOn: dayjs().format("YYYY-MM-DD"),
  note: "",
  logeEventId: null,
  logeMemberId: null,
});

export const transactionToForm = (t: FinanceTransaction): TransactionFormValues => ({
  direction: t.amount_ore >= 0 ? 1 : -1,
  amount: oreToInput(t.amount_ore),
  category: t.category,
  occurredOn: t.occurred_on,
  note: t.note ?? "",
  logeEventId: t.loge_event_id,
  logeMemberId: t.loge_member_id,
});

/** The signed payload, or null while the form isn't bookable yet. */
export function toPayload(values: TransactionFormValues): FinanceTransactionPayload | null {
  const ore = parseKrToOre(values.amount);
  if (ore === null || ore === 0 || !values.occurredOn) return null;
  return {
    occurredOn: values.occurredOn,
    // The direction toggle owns the sign, so a treasurer who types "-25"
    // under UD doesn't end up booking income.
    amountOre: Math.abs(ore) * values.direction,
    category: values.category,
    note: values.note,
    logeEventId: values.logeEventId,
    logeMemberId: values.logeMemberId,
  };
}

/**
 * Booking a movement.
 *
 * The direction is a control of its own rather than a minus sign in the
 * amount field. The ledger stores one signed number (see the migration), but
 * "did money come in or go out" is the question being answered, and asking it
 * with punctuation is how a ledger ends up with an income row for a bill.
 * Picking a category moves the toggle to that category's usual direction —
 * gebyrer go out, kontingenter come in — without locking it, because a
 * refunded deposit really is income booked against an arrangement.
 */
export function TransactionForm({
  values,
  onChange,
  events,
  members,
}: {
  values: TransactionFormValues;
  onChange: (values: TransactionFormValues) => void;
  events: LogeEvent[];
  members: LogeMember[];
}) {
  const set = <K extends keyof TransactionFormValues>(
    key: K,
    value: TransactionFormValues[K]
  ) => onChange({ ...values, [key]: value });

  const [previousDirection, setPreviousDirection] = useState<string | undefined>();
  const income = values.direction === 1;

  // The opening balance isn't offered as a choice, but editing the row that
  // holds it shouldn't show a type row with nothing selected in it — so it
  // joins the list when it's already the answer.
  const categories = BOOKABLE_CATEGORIES.includes(values.category)
    ? BOOKABLE_CATEGORIES
    : [values.category, ...BOOKABLE_CATEGORIES];

  const selectCategory = (category: FinanceCategory) => {
    const suggested = CATEGORY_DEFAULT_DIRECTION[category];
    if (suggested !== values.direction) setPreviousDirection(income ? "ind" : "ud");
    onChange({ ...values, category, direction: suggested });
  };

  return (
    <div className="flex flex-col gap-6">
      <VaultField label="Retning">
        <SegmentedTabs
          tone="vault"
          segments={[
            { key: "ind", label: "Ind" },
            { key: "ud", label: "Ud" },
          ]}
          activeKey={income ? "ind" : "ud"}
          enterFrom={previousDirection}
          onSelect={(key) => {
            setPreviousDirection(income ? "ind" : "ud");
            set("direction", key === "ind" ? 1 : -1);
          }}
        />
      </VaultField>

      <VaultField label="Beløb" hint="Ører skrives efter komma" htmlFor="finance-amount">
        <div
          className={`flex items-baseline gap-0 border-b-2 pb-2 ${
            income ? "border-mint" : "border-ember"
          }`}
        >
          <span
            className={`font-display font-extrabold text-[40px] leading-[0.85] ${
              income ? "text-mint" : "text-ember"
            }`}
          >
            {income ? "+" : "−"}
          </span>
          <input
            id="finance-amount"
            type="text"
            // `decimal` rather than `numeric`: it's the keypad that has a
            // separator key on it, which an amount in øre needs.
            inputMode="decimal"
            value={values.amount}
            onChange={(e) => set("amount", e.target.value)}
            placeholder="0,00"
            className="w-40 bg-transparent text-vault-ink placeholder:text-vault-ink3/50 font-display font-extrabold text-[48px] leading-[0.85] tracking-tight tabular-nums focus:outline-none ml-1"
          />
          <div className="font-mono text-sm font-semibold text-vault-ink3">KR.</div>
        </div>
      </VaultField>

      <VaultField label="Type">
        <div className="flex flex-wrap gap-1.5">
          {categories.map((category) => {
            const on = category === values.category;
            return (
              <button
                key={category}
                type="button"
                onClick={() => selectCategory(category)}
                className={`flex items-center gap-1.5 py-2 px-2.5 rounded-lg border font-mono text-[10.5px] tracking-wide uppercase ${
                  on
                    ? "bg-white/[0.13] border-white/25 text-vault-ink"
                    : "bg-white/[0.03] border-white/10 text-vault-ink3"
                }`}
              >
                <Icon name={CATEGORY_ICONS[category]} size={11} />
                {CATEGORY_LABELS[category]}
              </button>
            );
          })}
        </div>
      </VaultField>

      <VaultField label="Dato" htmlFor="finance-date">
        <input
          id="finance-date"
          type="date"
          value={values.occurredOn}
          onChange={(e) => set("occurredOn", e.target.value)}
          className="w-full bg-transparent border-b-2 border-white/20 pb-2 text-vault-ink text-base font-medium focus:outline-none"
          // iOS renders the date control's own text near-black by default,
          // which is invisible down here.
          style={{ colorScheme: "dark" }}
        />
      </VaultField>

      <VaultField label="Tekst" hint="Vises i bevægelseslisten">
        <input
          type="text"
          value={values.note}
          onChange={(e) => set("note", e.target.value)}
          placeholder={
            income ? "Fx “Kontingenter · oktober”" : "Fx “Arrangement · oktober”"
          }
          className="w-full bg-transparent border-b-2 border-white/20 pb-2 text-vault-ink placeholder:text-vault-ink3/60 text-base font-medium focus:outline-none"
        />
      </VaultField>

      {events.length > 0 && (
        <VaultField label="Samling · valgfrit" hint="Knytter beløbet til en aften">
          <div className="flex flex-wrap gap-1.5">
            {events.slice(0, 12).map((event) => {
              const on = values.logeEventId === event.id;
              return (
                <button
                  key={event.id}
                  type="button"
                  onClick={() => set("logeEventId", on ? null : event.id)}
                  className={`flex items-center gap-1.5 py-1.5 px-2.5 rounded-lg border font-mono text-[10.5px] tracking-wide uppercase max-w-full ${
                    on
                      ? "bg-white/[0.13] border-white/25 text-vault-ink"
                      : "bg-white/[0.03] border-white/10 text-vault-ink3"
                  }`}
                >
                  <span className="text-sm leading-none">{event.emoji}</span>
                  <span className="truncate">{event.title}</span>
                  <span className="opacity-60 flex-shrink-0">
                    {dayjs(event.date).format("MMM YY")}
                  </span>
                </button>
              );
            })}
          </div>
        </VaultField>
      )}

      <VaultField
        label="Arrangør / betaler · valgfrit"
        hint="Hvem posteringen vedrører"
      >
        <div className="flex flex-wrap gap-1.5">
          {members.map((member) => {
            const on = values.logeMemberId === member.id;
            return (
              <button
                key={member.id}
                type="button"
                onClick={() => set("logeMemberId", on ? null : member.id)}
                className={`flex items-center gap-1.5 py-1 pl-1 pr-2.5 rounded-lg border ${
                  on
                    ? "bg-white/[0.13] border-white/25 text-vault-ink"
                    : "bg-white/[0.03] border-white/10 text-vault-ink3"
                }`}
              >
                <span className="text-sm">{member.emoji}</span>
                <span className="font-mono text-[10.5px] tracking-wide font-medium uppercase">
                  {member.name}
                </span>
              </button>
            );
          })}
        </div>
      </VaultField>
    </div>
  );
}

/** The header every finance form screen wears — AdminHeader, in the dark. */
export function VaultHeader({
  back,
  onBack,
  title,
  sub,
  action,
}: {
  back: string;
  onBack: () => void;
  title: ReactNode;
  sub?: string;
  action?: ReactNode;
}) {
  return (
    <div className="px-7 pt-2">
      <div className="flex items-center justify-between">
        <button
          onClick={onBack}
          className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-vault-ink3 flex items-center gap-1.5"
        >
          <Icon name="chevron-left" size={12} />
          <span>{back}</span>
        </button>
        {action}
      </div>
      {sub && (
        <div className="font-mono text-[10.5px] tracking-[1.6px] uppercase text-vault-ink3 mt-4">
          {sub}
        </div>
      )}
      <div className="font-display text-[40px] leading-[0.9] tracking-tight font-extrabold mt-1.5 uppercase">
        {title}
      </div>
    </div>
  );
}
